import { useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import styles from "./ChatComposer.module.css";
import { cx } from "../cx";
import { Chip } from "../components/Chip";
import { IconBrick, IconChatLine, IconPaperplaneFill, IconSparkle2Fill } from "../icons/Icon";

export type ChatComposerProps = {
  value: string;
  onValueChange: (v: string) => void;
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** meta row */
  modelName?: string;
  messageCost?: number;
  freeTurnsLabel?: string;
  onModelClick?: () => void;
  /** 추천 답변 칩. 비우면 행이 사라진다. */
  suggestions?: readonly string[];
  onSuggestionClick?: (text: string) => void;
  /** ✨ 이미지 생성 버튼 (2차). */
  onImageClick?: () => void;
  /** ✦ 도구 버튼(1차: 대화 도구 시트). onImageClick과 같은 자리, 라벨만 다르다. */
  onToolsClick?: () => void;
  /** meta 행 우측 "✦ 추천 답변 받기 · n브릭" 버튼. 비우면 없음. */
  suggestAction?: { label: ReactNode; onClick: () => void; disabled?: boolean };
  /** 입력창 포커스(비회원이면 로그인 시트). */
  onFocus?: () => void;
  /** 엔터 전송·Shift+엔터 줄바꿈. 끄면 엔터는 줄바꿈이고 전송은 버튼으로만. */
  enterToSend?: boolean;
  className?: string;
};

/**
 * 채팅 입력부 (Figma 88:3118). idle → focused → typing(전송 활성).
 * 추천 칩을 누르면 onSuggestionClick, 없으면 입력창에 채운다.
 * 엔터는 기본 줄바꿈(textarea 기본 동작)이고 전송은 전송 버튼으로만 한다(AC0-231). enterToSend 면 엔터 전송(웹 데스크톱, AC0-284).
 */
export function ChatComposer({
  value,
  onValueChange,
  onSend,
  disabled = false,
  placeholder = "메시지 입력…  *행동*은 별표로",
  modelName,
  messageCost,
  freeTurnsLabel,
  onModelClick,
  suggestions,
  onSuggestionClick,
  onImageClick,
  onToolsClick,
  suggestAction,
  onFocus,
  enterToSend = false,
  className,
}: ChatComposerProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const canSend = value.trim().length > 0 && !disabled;
  const showMeta = modelName !== undefined || messageCost !== undefined || freeTurnsLabel !== undefined;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;
    onSend(value.trim());
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
  const pick = (s: string) => {
    if (onSuggestionClick) onSuggestionClick(s);
    else {
      onValueChange(s);
      inputRef.current?.focus();
    }
  };

  return (
    <form className={cx(styles.root, className)} onSubmit={submit}>
      {showMeta && (
        <div className={styles.meta}>
          {modelName !== undefined && (
            <button type="button" className={styles.modelChip} onClick={onModelClick} disabled={!onModelClick}>
              <IconChatLine size={14} />
              <span className={styles.modelName}>{modelName}</span>
              {onModelClick && <span className={styles.caret}>▾</span>}
            </button>
          )}
          <span className={styles.spacer} />
          {freeTurnsLabel && <span className={styles.free}>{freeTurnsLabel}</span>}
          {messageCost !== undefined && (
            <span className={styles.cost}>
              메시지당 <IconBrick size={12} /> <b>{messageCost}</b>
            </span>
          )}
        </div>
      )}
      {suggestAction && (
        /* Figma ChatComposer suggest-row: 입력창 위 한 줄, 점선 테두리 둥근 칩 "✦ 추천 답변 받기 · n브릭" */
        <div className={styles.suggestRow}>
          <button type="button" className={styles.suggestAction} onClick={suggestAction.onClick} disabled={suggestAction.disabled || disabled}>
            <IconSparkle2Fill size={14} />
            <span>{suggestAction.label}</span>
          </button>
        </div>
      )}
      {suggestions && suggestions.length > 0 && (
        <div className={styles.suggestions} data-testid="composer-chips">
          {suggestions.map((s) => (
            <Chip key={s} size="small" onClick={() => pick(s)} disabled={disabled}>
              {s}
            </Chip>
          ))}
        </div>
      )}
      <div className={styles.inputRow}>
        {onImageClick && (
          <button type="button" className={styles.tool} aria-label="장면 이미지 생성" onClick={onImageClick} disabled={disabled}>
            <IconSparkle2Fill size={20} />
          </button>
        )}
        {!onImageClick && onToolsClick && (
          <button type="button" className={styles.tool} aria-label="대화 도구" onClick={onToolsClick} disabled={disabled}>
            <IconSparkle2Fill size={20} />
          </button>
        )}
        <div className={cx(styles.field, focused && styles.fieldFocused)}>
          <textarea
            ref={inputRef}
            className={styles.textarea}
            rows={1}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onValueChange(e.target.value)}
            onFocus={() => {
              setFocused(true);
              onFocus?.();
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={enterToSend ? onKeyDown : undefined}
          />
          <button type="submit" className={cx(styles.send, canSend && styles.sendActive)} aria-label="전송" disabled={!canSend}>
            <IconPaperplaneFill size={18} />
          </button>
        </div>
      </div>
    </form>
  );
}
